import {memo} from 'react'
import {BranchName, NavList} from '@primer/react'
import {DotIcon, XIcon} from '@primer/octicons-react'
import {useClickAnalytics} from '@github-ui/use-analytics'

import type {PreviewStackPull} from '../../../page-data/payloads/preview-stack'
import {PullRequestState} from '../../../types'
import {StateIcon} from '../../StateIcon'
import {buildPullRequestUserActionContext} from '../../../analytics/pull-request-user-action'
import styles from './PreviewStackDialog.module.css'

type PreviewStackPullItemProps = {
  pull: PreviewStackPull
  isCurrent: boolean
  isTail: boolean
  onRemove?: () => void
  disableRemove: boolean
}

export const PreviewStackPullItem = memo(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name for readable stack traces
  function PreviewStackPullItem({pull, isCurrent, isTail, onRemove, disableRemove}: PreviewStackPullItemProps) {
    const {sendClickAnalyticsEvent} = useClickAnalytics()
    const destinationBranch = isTail ? pull.baseBranch : null

    return (
      <>
        <NavList.Item href={pull.url} aria-current={isCurrent ? 'page' : undefined}>
          <NavList.LeadingVisual>
            <StateIcon state={pull.state ?? PullRequestState.Open} />
          </NavList.LeadingVisual>
          {pull.title}
          <NavList.Description variant="block">
            #{pull.number} · {pull.headBranch}
          </NavList.Description>
          {onRemove ? (
            <NavList.TrailingAction
              as="button"
              icon={XIcon}
              label={
                isCurrent
                  ? "You can't remove the currently viewed pull request"
                  : `Remove #${pull.number} from preview stack`
              }
              disabled={disableRemove || isCurrent}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault()
                event.stopPropagation()
                sendClickAnalyticsEvent(
                  buildPullRequestUserActionContext({
                    category: 'stack_preview',
                    action: 'remove',
                  }),
                )
                onRemove()
              }}
            />
          ) : null}
        </NavList.Item>
        {isTail && destinationBranch ? (
          <li className={styles.branchItem}>
            <span className={styles.branchItemContent}>
              <DotIcon className="fgColor-muted" />
              <BranchName as="span">{destinationBranch}</BranchName>
            </span>
          </li>
        ) : null}
      </>
    )
  },
)
